/**
 * The office's side of the free lawn walkthrough, on the customer card.
 *
 * What is booked (when, who), the customer's own link to the sheet, and "Book one for them" off the same open
 * times the customer would see (services/walkthrough). A customer with no yard address is sent to add one on
 * the card first — the booking never takes a plain typed address from here.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { Footprints } from 'lucide-react';
import { api } from '../services/api';
import { useReadOnly } from '../hooks/useReadOnly';
import { SlotPicker, WalkStatus } from './LawnWalkthroughButton';

export default function LawnWalkthroughCard({ customerId, customerName }: { customerId: number; customerName?: string | null }) {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState('');
  const [time, setTime] = useState('');
  const [copied, setCopied] = useState(false);
  const { data, refetch } = useQuery<WalkStatus & { link: string | null }>(['walkthrough', customerId],
    () => api.get(`/walkthrough/customer/${customerId}`).then(r => r.data), { enabled: open });
  const book = useMutation(() => api.post(`/walkthrough/customer/${customerId}/book`, { date: day, time }).then(r => r.data), {
    onSuccess: (d: any) => {
      toast.success(d?.message || 'Booked');
      setDay(''); setTime(''); refetch();
      qc.invalidateQueries('calendar-jobs'); qc.invalidateQueries('bid-visits'); qc.invalidateQueries('field-stops'); qc.invalidateQueries(['cust-profile-jobs', customerId]);
    },
    onError: (e: any) => { const d = e?.response?.data; toast.error(d?.message || 'Could not book that'); if (d?.taken) { setTime(''); refetch(); } },
  });
  const copy = (text: string) => { navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const first = String(customerName || '').split(' ')[0] || 'them';

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="mt-2 text-xs font-semibold text-brand-700 underline inline-flex items-center gap-1">
        <Footprints className="h-3.5 w-3.5" /> Lawn walkthrough
      </button>
    );
  }
  return (
    <div className="mt-2 rounded-lg border border-brand-200 bg-white p-2.5 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-bold uppercase tracking-wide text-brand-700 inline-flex items-center gap-1"><Footprints className="h-3.5 w-3.5" /> Lawn walkthrough</p>
        <button type="button" onClick={() => setOpen(false)} className="text-xs text-gray-500 underline">close</button>
      </div>
      {!data ? <p className="text-xs text-gray-500">Looking…</p> : (
        <>
          {data.open ? (
            <p className="text-sm text-gray-900">Booked: <b>{data.open.when}</b>{data.open.address ? ` at ${data.open.address}` : ''} · {data.open.who} walks it. It is on the calendar and the Field page.</p>
          ) : data.needs_address ? (
            <p className="text-sm text-amber-800">No yard address yet — add the address on this card first, then book the walk.</p>
          ) : !data.can ? (
            <p className="text-sm text-gray-700">{data.message}</p>
          ) : (
            <>
              <p className="text-xs text-gray-600">Free, {data.who} walks it. Book it for {first} here, or send them the link and they pick a time themselves.</p>
              <SlotPicker slots={data.slots} day={day} time={time} onDay={setDay} onTime={setTime} who={data.who} />
              <button type="button" onClick={() => book.mutate()} disabled={readOnly || book.isLoading || !day || !time}
                className="press text-xs px-3 py-1.5 rounded-lg bg-emerald-700 text-white font-semibold disabled:opacity-50">
                {book.isLoading ? 'Booking…' : `Book it for ${first}`}
              </button>
              <span className="text-[11px] text-gray-500 ml-2">They get the confirmation text; you get one too.</span>
            </>
          )}
          {data.link && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <code className="text-[11px] bg-gray-50 border border-gray-200 rounded px-2 py-1 max-w-full truncate">{data.link}</code>
              <button type="button" onClick={() => copy(`Want your lawn to look better? Book a free walkthrough with us here: ${data.link}`)}
                className="text-xs px-2 py-1 rounded border border-brand-300 text-brand-700 font-semibold">{copied ? 'Copied' : 'Copy text to send'}</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
